'use client'

import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { Globe, IdCard, Mail, Menu, Phone, X } from 'lucide-react'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { allNav } from '@/config/portal-nav'
import type { MainNavKey, UtilityNavKey } from '@/config/portal-navigation'
import { NotificationsMenu } from './notifications-menu'
import { portalNavTriggerClassName } from './portal-nav-styles'
import { ProfileMenu } from './profile-menu'
import { PortalLogo, SidebarNav, SidebarSearch } from './portal-sidebar'

// Placeholder — will be replaced with the member's assigned rep from the backend.
const ensembleContact = {
  initials: 'AR',
  name: 'Arleen Rios',
  phone: '+1 212 545 7460',
  email: 'arleen.rios@ensembletravel.com',
}

function EnsembleContactCard() {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-lg"
          className={portalNavTriggerClassName}
          aria-label="Your Ensemble contact"
        >
          <IdCard className="size-6" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={16}
        className="w-[22rem] max-w-[calc(100vw-2rem)] p-5"
      >
        <div className="mb-4 flex items-center justify-between gap-2">
          <h3>Your Ensemble Contact</h3>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="text-muted-foreground hover:text-foreground -m-1 shrink-0 rounded-sm p-1 transition-colors"
          >
            <X className="size-5" />
          </button>
        </div>
        <div className="flex items-start gap-3">
          <Avatar className="size-14 shrink-0">
            <AvatarFallback className="text-lg font-semibold">
              {ensembleContact.initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 space-y-1.5 pt-0.5">
            <p className="text-sm font-semibold">{ensembleContact.name}</p>
            <a
              href={`tel:${ensembleContact.phone.replace(/[^+\d]/g, '')}`}
              className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-sm transition-colors"
            >
              <Phone className="size-3.5 shrink-0" />
              {ensembleContact.phone}
            </a>
            <a
              href={`mailto:${ensembleContact.email}`}
              className="text-link link-hover-underline flex items-start gap-1.5 text-sm"
            >
              <Mail className="mt-0.5 size-3.5 shrink-0" />
              <span className="wrap-anywhere">{ensembleContact.email}</span>
            </a>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function PortalHeader({
  mainOrder,
  utilityOrder,
}: {
  mainOrder?: readonly MainNavKey[]
  utilityOrder?: readonly UtilityNavKey[]
}) {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  const current = allNav.find(
    (item) => pathname === item.href || pathname.startsWith(`${item.href}/`),
  )
  const title = current?.label ?? 'Home'

  return (
    <header className="bg-background/95 supports-[backdrop-filter]:bg-background/80 sticky top-0 z-30 px-4 py-4 shadow-md backdrop-blur sm:px-5 md:px-10 md:py-5">
      <div className="flex min-w-0 flex-col gap-3 md:flex-row md:items-center md:gap-3">
        {/* Top chrome row (menu / logo / actions). Title drops to its own row below md. */}
        <div className="flex min-w-0 items-center gap-2 sm:gap-3 md:contents">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon-lg"
                className={`${portalNavTriggerClassName} md:hidden`}
                aria-label="Open navigation"
              >
                <Menu className="size-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-sidebar w-72 gap-4 p-4">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <PortalLogo className="text-brand-maroon shrink-0" />
              <div className="shrink-0">
                <SidebarSearch />
              </div>
              {/*
                The nav list is the sheet's only scroll area: it takes the space the
                logo/search leave (`flex-1`) and scrolls inside the fixed panel. `min-h-0`
                is required — without it a flex item won't shrink below its content, so
                the list would spill off the bottom of the panel with nothing to scroll it.
              */}
              <div className="min-h-0 flex-1 overflow-y-auto">
                <SidebarNav
                  mainOrder={mainOrder}
                  utilityOrder={utilityOrder}
                  onNavigate={() => setOpen(false)}
                />
              </div>
            </SheetContent>
          </Sheet>

          <PortalLogo className="text-brand-maroon shrink-0 md:hidden" />

          <div className="ml-auto flex shrink-0 items-center gap-0.5 sm:gap-1.5 md:order-last md:ml-0">
            <Button
              variant="ghost"
              size="sm"
              className={`${portalNavTriggerClassName} h-11 gap-1.5 px-2 sm:px-3`}
              aria-label="Language"
            >
              <Globe className="size-6" />
              FR
            </Button>
            <EnsembleContactCard />
            <NotificationsMenu />
            <ProfileMenu />
          </div>
        </div>

        <h1 className="min-w-0 flex-1 truncate md:mr-auto">{title}</h1>
      </div>
    </header>
  )
}
